<template>
  <div class="maquinas-container">
    <!-- ✅ HEADER PRINCIPAL -->
    <div class="page-header">
      <div class="d-flex flex-column">
        <div class="d-flex align-center flex-wrap">
          <div class="d-flex align-center">
            <v-avatar size="48" color="blue-darken-1" class="mr-3" variant="tonal">
              <v-icon color="white" size="28">mdi-cog</v-icon>
            </v-avatar>
            <div>
              <h1 class="text-h4 font-weight-bold text-grey-darken-4 mb-1">Consulta de Máquinas</h1>
              <div class="text-caption text-grey">
                Gerencie e visualize todas as máquinas cadastradas
              </div>
            </div>
          </div>
          
          <v-spacer></v-spacer>
          
          <div class="d-flex align-center mt-2 mt-sm-0">
            <v-btn
              color="grey-darken-1"
              variant="text"
              @click="voltarMenu"
              class="text-none mr-2"
              prepend-icon="mdi-arrow-left"
            >
              Voltar
            </v-btn>
            
            <v-btn
              color="blue-darken-1"
              variant="flat"
              @click="fetchData"
              class="text-none"
              prepend-icon="mdi-refresh"
              :loading="loadingDialog"
            >
              Atualizar
            </v-btn>
          </div>
        </div>
        

      </div>
    </div>



    <!-- ✅ FILTROS EXPANSÍVEIS (mantido igual) -->
<!-- ✅ FILTROS EXPANSÍVEIS RESPONSIVOS -->
<v-card class="filters-card" elevation="2">
  <v-card-title class="filters-header pa-4">
    <div class="d-flex align-center w-100">
      <!-- ✅ SEÇÃO ESQUERDA - TÍTULO E INFORMAÇÕES -->
      <div class="d-flex align-center cursor-pointer flex-grow-1" @click="toggleFilters">
        <v-icon class="mr-2" color="white">mdi-filter-variant</v-icon>
        <span class="filters-title"></span>
        
        <!-- Chips de Status - Responsivos -->
        <div class="d-flex align-center gap-2 ml-4" :class="{ 'd-none d-sm-flex': isMobile }">
          <v-chip variant="tonal" size="small">
            {{ filteredMaquinas.length }} máquinas
          </v-chip>
          
          <v-chip 
            v-if="hasActiveFilters"
            color="warning" 
            variant="tonal" 
            size="small"
          >
            {{ activeFiltersCount }} filtro(s)
          </v-chip>
        </div>
      </div>
      
      <!-- ✅ SEÇÃO CENTRAL - CONTROLES DE AÇÃO RESPONSIVOS -->
      <div class="controls-section" :class="{ 'mobile-controls': isMobile }">
        <!-- Desktop Controls -->
        <div v-if="!isMobile" class="d-flex align-center gap-3 mr-4">
          <!-- Botão Atualizar -->
          <v-btn
            color="white"
            variant="flat"
            size="small"
            @click="fetchData"
            :loading="loadingDialog"
            class="compact-btn"
          >
            <v-icon size="18" color="grey-darken-2">mdi-refresh</v-icon>
            <span class="ml-2 text-grey-darken-2">Atualizar</span>
          </v-btn>
          
          <!-- Botão Limpar Filtros -->
          <v-btn
            color="white"
            variant="flat"
            size="small"
            @click="limparFiltros"
            :disabled="!hasActiveFilters"
            class="compact-btn ml-2 mr-2"
          >
            <v-icon size="18" :color="hasActiveFilters ? 'grey-darken-2' : 'grey-lighten-1'">mdi-filter-off</v-icon>
            <span class="ml-2" :class="hasActiveFilters ? 'text-grey-darken-2' : 'text-grey-lighten-1'">Limpar</span>
          </v-btn>

          <!-- Toggle de Visualização -->
          <v-btn-toggle
            v-model="viewMode"
            mandatory
            variant="outlined"
            divided
            size="small"
            class="compact-toggle ml-2"
            color="grey-darken-2"
          >
            <v-btn value="table" size="small">
              <v-icon size="18">mdi-table</v-icon>
              <v-tooltip activator="parent">Visualização em Tabela</v-tooltip>
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn value="cards" size="small" class="ml-2">
              <v-icon size="18">mdi-view-grid</v-icon>
              <v-tooltip activator="parent">Visualização em Cards</v-tooltip>
            </v-btn>
          </v-btn-toggle>
        </div>

        <!-- Mobile Controls -->
        <div v-else class="d-flex align-center gap-2">
          <!-- Botão Atualizar Mobile -->
          <v-btn
            color="white"
            variant="flat"
            size="x-small"
            @click="fetchData"
            :loading="loadingDialog"
            class="mobile-btn"
            icon
          >
            <v-icon size="16" color="grey-darken-2">mdi-refresh</v-icon>
            <v-tooltip activator="parent">Atualizar</v-tooltip>
          </v-btn>
          
          <!-- Botão Limpar Filtros Mobile -->
          <v-btn
            color="white"
            variant="flat"
            size="x-small"
            @click="limparFiltros"
            :disabled="!hasActiveFilters"
            class="mobile-btn"
            icon
          >
            <v-icon size="16" :color="hasActiveFilters ? 'grey-darken-2' : 'grey-lighten-1'">mdi-filter-off</v-icon>
            <v-tooltip activator="parent">Limpar Filtros</v-tooltip>
          </v-btn>

          <!-- Menu de Visualização Mobile -->
          <v-menu offset-y>
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                color="white"
                variant="flat"
                size="x-small"
                class="mobile-btn"
                icon
              >
                <v-icon size="16" color="grey-darken-2">{{ viewMode === 'table' ? 'mdi-table' : 'mdi-view-grid' }}</v-icon>
                <v-tooltip activator="parent">Modo de Visualização</v-tooltip>
              </v-btn>
            </template>
            <v-list density="compact" class="mobile-menu">
              <v-list-item @click="viewMode = 'table'" :class="{ 'v-list-item--active': viewMode === 'table' }">
                <template v-slot:prepend>
                  <v-icon size="18">mdi-table</v-icon>
                </template>
                <v-list-item-title>Tabela</v-list-item-title>
              </v-list-item>
              <v-list-item @click="viewMode = 'cards'" :class="{ 'v-list-item--active': viewMode === 'cards' }">
                <template v-slot:prepend>
                  <v-icon size="18">mdi-view-grid</v-icon>
                </template>
                <v-list-item-title>Cards</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
      
      <!-- ✅ SEÇÃO DIREITA - BOTÃO DE EXPANDIR -->
      <v-btn
        icon
        variant="text"
        :size="isMobile ? 'x-small' : 'small'"
        color="white"
        class="expand-btn ml-2"
        @click="toggleFilters"
      >
        <v-icon :size="isMobile ? 16 : 20" :class="{ 'rotate-180': filtersExpanded }">
          mdi-chevron-down
        </v-icon>
        <v-tooltip activator="parent">
          {{ filtersExpanded ? 'Recolher Filtros' : 'Expandir Filtros' }}
        </v-tooltip>
      </v-btn>
    </div>

    <!-- ✅ CHIPS DE STATUS MOBILE (abaixo do header) -->
    <div v-if="isMobile" class="mobile-status-chips pa-2 pt-0">
      <div class="d-flex align-center flex-wrap gap-1">
        <v-chip variant="tonal" size="x-small" color="blue-grey">
          {{ filteredMaquinas.length }} máquinas
        </v-chip>
        
        <v-chip 
          v-if="hasActiveFilters"
          color="warning" 
          variant="tonal" 
          size="x-small"
        >
          {{ activeFiltersCount }} filtro(s)
        </v-chip>
      </div>
    </div>
  </v-card-title>
  
  <!-- ✅ CONTEÚDO EXPANSÍVEL DOS FILTROS -->
  <v-expand-transition>
    <div v-show="filtersExpanded">
      <v-divider></v-divider>
      
      <v-card-text class="pa-6">
        <v-row>
          <!-- Campo de Busca -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model="search"
              label="Buscar máquinas..."
              placeholder="Digite ID (MAQ001), descrição, série..."
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              clearable
              hide-details
              :density="isMobile ? 'compact' : 'default'"
              class="search-field"
            >
              <template v-slot:append-inner>
                <v-tooltip text="Busque por ID (MAQ001), descrição, número de série, tipo ou setor">
                  <template v-slot:activator="{ props }">
                    <v-icon v-bind="props" size="20" color="grey">mdi-help-circle-outline</v-icon>
                  </template>
                </v-tooltip>
              </template>
            </v-text-field>
          </v-col>

          <!-- Filtro Tipo de Máquina -->
          <v-col cols="12" md="3">
            <v-select
              v-model="filtroTipoMaquina"
              :items="tiposDeMaquina"
              label="Tipo de Máquina"
              variant="outlined"
              clearable
              hide-details
              :density="isMobile ? 'compact' : 'default'"
              prepend-inner-icon="mdi-cog"
            ></v-select>
          </v-col>

          <!-- Filtro Setor -->
          <v-col cols="12" md="3">
            <v-select
              v-model="filtroSetor"
              :items="setores"
              label="Setor"
              variant="outlined"
              clearable
              hide-details
              :density="isMobile ? 'compact' : 'default'"
              prepend-inner-icon="mdi-domain"
            ></v-select>
          </v-col>
        </v-row>

        <!-- Botões de Ação - Apenas Mobile quando expandido -->
        <v-row v-if="isMobile" class="mt-4">
          <v-col cols="12" class="d-flex flex-wrap gap-2">
            <v-btn
              color="primary"
              variant="flat"
              size="small"
              @click="fetchData"
              :loading="loadingDialog"
            >
              <v-icon start size="18">mdi-refresh</v-icon>
              Atualizar
            </v-btn>
            
            <v-btn
              color="warning"
              variant="outlined"
              size="small"
              @click="limparFiltros"
              :disabled="!hasActiveFilters"
            >
              <v-icon start size="18">mdi-filter-off</v-icon>
              Limpar
            </v-btn>

            <v-spacer></v-spacer>

            <!-- Toggle de Visualização Mobile -->
            <v-btn-toggle
              v-model="viewMode"
              mandatory
              variant="outlined"
              divided
              size="small"
            >
              <v-btn value="table" size="small">
                <v-icon size="18">mdi-table</v-icon>
              </v-btn>
              <v-btn value="cards" size="small">
                <v-icon size="18">mdi-view-grid</v-icon>
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>
      </v-card-text>
    </div>
  </v-expand-transition>
  
  <!-- ✅ BARRA DE FILTROS RÁPIDOS (quando recolhido) -->
  <div v-if="!filtersExpanded && hasActiveFilters" class="quick-filters-bar">
    <v-divider></v-divider>
    <div class="pa-3">
      <div class="d-flex align-center flex-wrap gap-2">
        <span class="text-caption text-grey mr-2">Filtros ativos:</span>
        
        <!-- Chip de Busca -->
        <v-chip
          v-if="search"
          color="primary"
          variant="tonal"
          :size="isMobile ? 'x-small' : 'small'"
          closable
          @click:close="search = ''"
        >
          <v-icon start :size="isMobile ? 12 : 16">mdi-magnify</v-icon>
          "{{ search }}"
        </v-chip>
        
        <!-- Chip de Tipo -->
        <v-chip
          v-if="filtroTipoMaquina"
          color="orange-darken-1"
          variant="tonal"
          :size="isMobile ? 'x-small' : 'small'"
          closable
          @click:close="filtroTipoMaquina = null"
        >
          <v-icon start :size="isMobile ? 12 : 16">mdi-cog</v-icon>
          {{ filtroTipoMaquina }}
        </v-chip>
        
        <!-- Chip de Setor -->
        <v-chip
          v-if="filtroSetor"
          color="green-darken-1"
          variant="tonal"
          :size="isMobile ? 'x-small' : 'small'"
          closable
          @click:close="filtroSetor = null"
        >
          <v-icon start :size="isMobile ? 12 : 16">mdi-domain</v-icon>
          {{ filtroSetor }}
        </v-chip>
        
        <v-spacer></v-spacer>
        
        <!-- Botão Limpar Tudo -->
        <v-btn
          color="warning"
          variant="text"
          :size="isMobile ? 'x-small' : 'small'"
          @click="limparFiltros"
        >
          <v-icon start :size="isMobile ? 12 : 16">mdi-close</v-icon>
          <span v-if="!isMobile">Limpar Tudo</span>
        </v-btn>
      </div>
    </div>
  </div>
</v-card>
    <!-- ✅ CONTEÚDO PRINCIPAL -->
    <div v-if="!loadingDialog" class="content-area" :class="{ 'expanded': !filtersExpanded }">
      <!-- ✅ VISUALIZAÇÃO EM TABELA (Desktop) - COM SCROLL HORIZONTAL E COLUNA FIXA -->
      <v-card v-if="!isMobile && viewMode === 'table'" class="table-card" elevation="2">
        <!-- ✅ INDICADOR DE SCROLL HORIZONTAL -->
        <div v-if="showScrollIndicator" class="scroll-indicator">
          <v-alert 
            type="info" 
            variant="tonal" 
            density="compact" 
            class="ma-2"
          >
            <v-icon start>mdi-arrow-left-right</v-icon>
            Use o scroll horizontal para ver mais colunas. A coluna ID permanece fixa.
          </v-alert>
        </div>

        <div class="table-wrapper">
          <v-data-table
            :headers="headers"
            :items="filteredMaquinas"
              fixed-header
             class="elevation-0 scrollable-table"
            item-key="id"
          >
            <!-- ✅ COLUNA ID FIXA -->
            <template v-slot:item.id="{ item }">
              <div class="fixed-column-content">
                <v-chip color="blue-darken-1" variant="tonal" size="small">
                  MAQ{{ formatId(item.id) }}
                </v-chip>
              </div>
            </template>

            <template v-slot:item.descricao_maquina="{ item }">
              <div class="font-weight-medium text-truncate" style="max-width: 200px;">
                {{ item.descricao_maquina }}
              </div>
            </template>

            <template v-slot:item.numero_serie="{ item }">
              <span class="text-grey-darken-1 text-truncate" style="max-width: 150px;">
                {{ item.numero_serie || "Não informado" }}
              </span>
            </template>

            <template v-slot:item.tipo_maquina="{ item }">
              <v-chip color="orange-darken-1" variant="tonal" size="small">
                {{ item.tipo_maquina?.tipo_maquinas || "Não especificado" }}
              </v-chip>
            </template>

            <template v-slot:item.setor="{ item }">
              <v-chip color="green-darken-1" variant="tonal" size="small">
                {{ item.setor?.descricao_setor || "Não especificado" }}
              </v-chip>
            </template>

            <template v-slot:item.localizacao="{ item }">
              <div v-if="item.fila || item.posicao" class="d-flex align-center">
                <v-icon size="16" class="mr-1" color="grey">mdi-map-marker</v-icon>
                F{{ item.fila || "-" }} / P{{ item.posicao || "-" }}
              </div>
              <span v-else class="text-grey">Não definido</span>
            </template>

            <template v-slot:item.actions="{ item }">
              <div class="d-flex gap-1">
                <v-btn
                  icon
                  size="small"
                  color="primary"
                  variant="tonal"
                  @click="openModal(item)"
                >
                  <v-icon size="18">mdi-eye</v-icon>
                  <v-tooltip activator="parent">Ver Detalhes</v-tooltip>
                </v-btn>
                
                <v-btn
                  icon
                  size="small"
                  color="secondary"
                  variant="tonal"
                  @click="imprimirEtiqueta(item)"
                >
                  <v-icon size="18">mdi-barcode</v-icon>
                  <v-tooltip activator="parent">Imprimir Etiqueta</v-tooltip>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </div>
      </v-card>

      <!-- ✅ VISUALIZAÇÃO EM CARDS (Mobile e Desktop) - COM PAGINAÇÃO MELHORADA -->
      <div v-if="isMobile || viewMode === 'cards'" class="cards-container">
        <!-- ✅ INDICADOR DE PÁGINA NO TOPO (MOBILE) -->
        <div v-if="isMobile && filteredMaquinas.length > itemsPerPage" class="mobile-page-indicator mb-4">
          <v-card class="page-info-card" elevation="1">
            <v-card-text class="pa-3">
              <div class="d-flex align-center justify-space-between">
                <div class="page-info">
                  <v-icon color="blue-darken-1" class="mr-2">mdi-view-grid</v-icon>
                  <span class="text-body-2 font-weight-medium">
                    Página {{ currentPage }} de {{ totalPages }}
                  </span>
                </div>
                <div class="page-count">
                  <v-chip color="blue-darken-1" variant="tonal" size="small">
                    {{ startCardItem }}-{{ endCardItem }} de {{ filteredMaquinas.length }}
                  </v-chip>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </div>



        <!-- ✅ ÁREA DE SCROLL DOS CARDS -->
        <div class="cards-scroll-area" :style="{ maxHeight: dynamicCardsHeight }">
          <v-row>
            <v-col
              v-for="maquina in paginatedCardItems"
              :key="maquina.id"
              cols="12"
              :md="viewMode === 'cards' ? 6 : 12"
              :lg="viewMode === 'cards' ? 3 : 12"
            >
              <v-card class="device-card" elevation="3" hover>
                <!-- Header do Card -->
                <v-card-title class="device-card-header pa-4">
                  <div class="d-flex align-center w-100">
                    <v-avatar size="40" color="blue-darken-1" variant="tonal" class="mr-3">
                      <v-icon color="white">mdi-cog</v-icon>
                    </v-avatar>
                    <div class="flex-grow-1">
                      <div class="text-h6 font-weight-bold">{{ maquina.descricao_maquina }}</div>
                      <v-chip color="blue-darken-1" variant="tonal" size="x-small" class="mt-1">
                        MAQ{{ formatId(maquina.id) }}
                      </v-chip>
                    </div>
                    <v-chip 
                      v-if="maquina.status"
                      :color="getStatusColor(maquina.status).color"
                      variant="flat"
                      size="small"
                      class="status-chip"
                    >
                      {{ getStatusText(maquina.status) }}
                    </v-chip>
                  </div>
                </v-card-title>

                <v-divider></v-divider>

                <!-- Conteúdo do Card -->
                <v-card-text class="pa-4">
                  <v-row dense class="gy-4">
                    <!-- Número de Série -->
                    <v-col cols="12" class="info-item">
                      <div class="info-label">
                        <v-icon size="16" class="mr-1" color="indigo-darken-1">mdi-identifier</v-icon>
                        Número de Série
                      </div>
                      <div class="info-value">{{ maquina.numero_serie || "Não informado" }}</div>
                    </v-col>

                    <!-- Tipo de Máquina -->
                    <v-col cols="12" sm="6" class="info-item">
                      <div class="info-label">
                        <v-icon size="16" class="mr-1" color="orange-darken-1">mdi-cog</v-icon>
                        Tipo
                      </div>
                      <div class="info-value">
                        <v-chip color="orange-darken-1" variant="tonal" size="small">
                          {{ maquina.tipo_maquina?.tipo_maquinas || "Não especificado" }}
                        </v-chip>
                      </div>
                    </v-col>

                    <!-- Setor -->
                    <v-col cols="12" sm="6" class="info-item">
                      <div class="info-label">
                        <v-icon size="16" class="mr-1" color="green-darken-1">mdi-domain</v-icon>
                        Setor
                      </div>
                      <div class="info-value">
                        <v-chip color="green-darken-1" variant="tonal" size="small">
                          {{ maquina.setor?.descricao_setor || "Não especificado" }}
                        </v-chip>
                      </div>
                    </v-col>

                    <!-- Localização -->
                    <v-col cols="12" class="info-item">
                      <div class="info-label">
                        <v-icon size="16" class="mr-1" color="blue-darken-1">mdi-map-marker</v-icon>
                        Localização
                      </div>
                      <div class="info-value">
                        <v-chip
                          v-if="maquina.fila || maquina.posicao"
                          color="blue-darken-1"
                          variant="tonal"
                          size="small"
                        >
                          Fila {{ maquina.fila || "-" }} / Posição {{ maquina.posicao || "-" }}
                        </v-chip>
                        <span v-else class="text-grey">Não definido</span>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>

                <v-divider></v-divider>

                <!-- Ações do Card -->
                <v-card-actions class="pa-4 d-flex justify-space-between">
                  <v-btn
                    color="blue-darken-1"
                    variant="flat"
                    size="small"
                    @click="openModal(maquina)"
                    class="text-none flex-grow-1 mr-2"
                  >
                    <v-icon start size="18">mdi-eye</v-icon>
                    Detalhes
                  </v-btn>
                  
                  <v-btn
                    color="blue-darken-1"
                    variant="tonal"
                    size="small"
                    @click="imprimirEtiqueta(maquina)"
                    class="text-none flex-grow-1"
                  >
                    <v-icon start size="18">mdi-barcode</v-icon>
                    Etiqueta
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- ✅ PAGINAÇÃO PRINCIPAL (DESKTOP E MOBILE) -->
        <div v-if="filteredMaquinas.length > itemsPerPage" class="pagination-container mt-6">
          <!-- ✅ NAVEGAÇÃO MOBILE SIMPLIFICADA -->
          <div v-if="isMobile" class="mobile-pagination">
            <v-card class="pagination-card" elevation="2">
              <v-card-text class="pa-4">
                <!-- Botões de Navegação -->
                <div class="d-flex align-center justify-space-between gap-2">
                  <v-btn
                    :disabled="currentPage === 1"
                    color="blue-darken-1"
                    variant="flat"
                    size="large"
                    @click="currentPage = 1"
                    class="nav-btn"
                    min-width="40"
                  >
                    <v-icon>mdi-page-first</v-icon>
                  </v-btn>
                  
                  <v-btn
                    :disabled="currentPage === 1"
                    color="blue-darken-1"
                    variant="tonal"
                    size="large"
                    @click="currentPage--"
                    class="nav-btn"
                    min-width="40"
                  >
                    <v-icon>mdi-chevron-left</v-icon>
                  </v-btn>

                  <!-- Indicador de Página -->
                  <v-chip color="blue-darken-1" variant="flat" class="px-4">
                    {{ currentPage }}/{{ totalPages }}
                  </v-chip>

                  <v-btn
                    :disabled="currentPage === totalPages"
                    color="blue-darken-1"
                    variant="tonal"
                    size="large"
                    @click="currentPage++"
                    class="nav-btn"
                    min-width="40"
                  >
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-btn>

                  <v-btn
                    :disabled="currentPage === totalPages"
                    color="blue-darken-1"
                    variant="flat"
                    size="large"
                    @click="currentPage = totalPages"
                    class="nav-btn"
                    min-width="40"
                  >
                    <v-icon>mdi-page-last</v-icon>
                  </v-btn>
                </div>
                
                <!-- Slider de Página (Mobile) -->
                <div class="page-slider">
                  <v-slider
                    v-model="currentPage"
                    :min="1"
                    :max="totalPages"
                    :step="1"
                    color="blue-darken-1"
                    track-color="blue-lighten-4"
                    thumb-color="blue-darken-1"
                    hide-details
                    class="page-slider-component"
                  >
                    <template v-slot:prepend>
                      <span class="text-caption">1</span>
                    </template>
                    <template v-slot:append>
                      <span class="text-caption">{{ totalPages }}</span>
                    </template>
                  </v-slider>
                </div>
                
                <!-- Informações de Itens -->
                <div class="text-center mt-3">
                  <span class="text-body-2 text-grey">
                    Exibindo {{ startCardItem }}-{{ endCardItem }} de {{ filteredMaquinas.length }} máquinas
                  </span>
                </div>
              </v-card-text>
            </v-card>
          </div>
          
          <!-- ✅ PAGINAÇÃO DESKTOP -->
          <div v-else class="d-flex align-center justify-space-between">
            <span class="text-body-2 text-grey">
              Exibindo {{ startCardItem }}-{{ endCardItem }} de {{ filteredMaquinas.length }} itens
            </span>
            
            <div class="d-flex align-center gap-2">
              <v-btn
                :disabled="currentPage === 1"
                color="blue-darken-1"
                variant="text"
                size="small"
                @click="currentPage = 1"
                class="text-none"
                min-width="40"
              >
                <v-icon>mdi-page-first</v-icon>
              </v-btn>
              
              <v-pagination
                v-model="currentPage"
                :length="totalPages"
                :total-visible="5"
                color="blue-darken-1"
                class="mb-0"
              ></v-pagination>
              
              <v-btn
                :disabled="currentPage === totalPages"
                color="blue-darken-1"
                variant="text"
                size="small"
                @click="currentPage = totalPages"
                class="text-none"
                min-width="40"
              >
                <v-icon>mdi-page-last</v-icon>
              </v-btn>
            </div>
            
            <div class="d-flex align-center" style="width: 150px;">
              <span class="text-caption text-grey mr-2">Itens por página:</span>
              <v-select
                v-model="itemsPerPage"
                :items="[10, 25, 50, 100]"
                density="compact"
                variant="outlined"
                hide-details
                class="page-size-select"
              ></v-select>
            </div>
          </div>
        </div>

        <!-- ✅ BOTÃO FLUTUANTE DE NAVEGAÇÃO (MOBILE) -->
        <div v-if="isMobile && filteredMaquinas.length > itemsPerPage" class="floating-nav">
          <v-btn
            v-if="currentPage > 1"
            color="blue-darken-1"
            variant="flat"
            size="small"
            @click="currentPage--"
            class="floating-btn floating-prev"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          
          <v-btn
            v-if="currentPage < totalPages"
            color="blue-darken-1"
            variant="flat"
            size="small"
            @click="currentPage++"
            class="floating-btn floating-next"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Mensagem quando não há resultados -->
      <v-card v-if="filteredMaquinas.length === 0" class="no-results-card" elevation="2">
        <v-card-text class="text-center pa-8">
          <v-avatar size="64" color="grey-lighten-3" class="mb-4">
            <v-icon size="32" color="grey">mdi-magnify-close</v-icon>
          </v-avatar>
          <div class="text-h6 mb-2">Nenhuma máquina encontrada</div>
          <div class="text-body-2 text-grey mb-4">
            Tente ajustar os filtros ou termos de busca
          </div>
          <v-btn color="primary" variant="outlined" @click="limparFiltros">
            Limpar Filtros
          </v-btn>
        </v-card-text>
      </v-card>
    </div>

    <!-- ✅ MODAIS (mantidos iguais) -->
    
    <!-- Modal de Carregamento -->
    <v-dialog v-model="loadingDialog" persistent max-width="400">
      <v-card class="loading-card">
        <v-card-text class="text-center pa-6">
          <v-progress-circular 
            indeterminate 
            color="blue-darken-1" 
            size="48"
            width="4"
            class="mb-4"
          ></v-progress-circular>
          <div class="text-h6 font-weight-medium mb-2">Processando...</div>
          <div class="text-body-2 text-grey">{{ mensagemDialag }}</div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Modal de Confirmação de Etiqueta -->
    <v-dialog v-model="dialogConfirmarEtiqueta" persistent max-width="500">
      <v-card class="confirmation-card">
        <v-card-title class="bg-secondary text-white pa-4">
          <v-icon class="mr-2">mdi-barcode</v-icon>
          Confirmar Impressão de Etiqueta
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <div class="d-flex align-center mb-4">
            <v-avatar size="48" color="secondary" variant="tonal" class="mr-4">
              <v-icon color="secondary">mdi-printer</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-medium">Imprimir etiqueta</div>
              <div class="text-body-2 text-grey">
                Máquina ID: <strong>MAQ{{ formatId(maquinaEtiquetaSelecionada) }}</strong>
              </div>
            </div>
          </div>
          
          <v-alert type="info" variant="tonal" class="mb-4">
            A etiqueta será enviada para a impressora padrão do sistema.
          </v-alert>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="outlined"
            @click="fecharModalEtiquita"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="secondary"
            variant="flat"
            @click="confirmaImpressaoEtiqueta"
            :loading="loadingDialog"
            class="ml-2"
          >
            <v-icon start>mdi-printer</v-icon>
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Detalhes da Máquina -->
    <ComponenteMaquinaDialog
      :idMaquina="selectedItemId"
      :ativa="modalVisible"
      :habilitarSelecao="false"
      @update:ativa="modalVisible = $event"
      @close="closeModal"
      @maquina-selecionada="handleMaquinaSelecionada"
    />
  </div>
</template>

<script>
import ComponenteMaquinaDialog from "@/components/ComponenteMaquinaDialog.vue";
import { maquinaService } from "@/services/maquinaService";
import { useDisplay } from "vuetify";
import { useToast } from "vue-toastification";

const toast = useToast();

export default {
  name: 'ConsultaMaquinas',
  
  components: {
    ComponenteMaquinaDialog,
  },

  data() {
    return {
      // Estados principais
      maquinas: [],
      loadingDialog: false,
      mensagemDialag: "",
      
      // ✅ CONTROLE DOS FILTROS EXPANSÍVEIS
      filtersExpanded: false,
      
      // ✅ CONTROLE DO INDICADOR DE SCROLL
      showScrollIndicator: true,
      
      // Modal states
      modalVisible: false,
      selectedItemId: null,
      dialogConfirmarEtiqueta: false,
      maquinaEtiquetaSelecionada: null,
      
      // Filtros e pesquisa
      search: "",
      filtroTipoMaquina: null,
      filtroSetor: null,
      
      // ✅ VISUALIZAÇÃO E PAGINAÇÃO
      viewMode: "table",
      currentPage: 1,
      itemsPerPage: 10,
      
      // ✅ Headers da tabela com larguras definidas
      headers: [
        { 
          title: "ID", 
          key: "id", 
          sortable: true, 
          width: "120px",
          fixed: true // ✅ Coluna fixa
        },
        { 
          title: "Descrição", 
          key: "descricao_maquina", 
          sortable: true, 
          width: "200px"
        },
        { 
          title: "Número de Série", 
          key: "numero_serie", 
          sortable: true, 
          width: "150px"
        },
        { 
          title: "Tipo", 
          key: "tipo_maquina", 
          sortable: true, 
          width: "180px"
        },
        { 
          title: "Setor", 
          key: "setor", 
          sortable: true, 
          width: "150px"
        },
        { 
          title: "Localização", 
          key: "localizacao", 
          sortable: false, 
          width: "140px"
        },
        { 
          title: "Ações", 
          key: "actions", 
          sortable: false, 
          align: "center", 
          width: "120px"
        },
      ],
    };
  },

  computed: {
    isMobile() {
      const { xs, sm, md } = useDisplay();
      return xs.value && !sm.value && !md.value;
    },

    // ✅ CONTROLE DE FILTROS ATIVOS
    hasActiveFilters() {
      return !!(this.search || this.filtroTipoMaquina || this.filtroSetor);
    },

    activeFiltersCount() {
      let count = 0;
      if (this.search) count++;
      if (this.filtroTipoMaquina) count++;
      if (this.filtroSetor) count++;
      return count;
    },

    // ✅ ALTURA DINÂMICA
    dynamicTableHeight() {
      if (this.filtersExpanded) {
        return this.showScrollIndicator ? 'calc(100vh - 500px)' : 'calc(100vh - 450px)';
      } else {
        return this.hasActiveFilters ? 'calc(100vh - 370px)' : 'calc(100vh - 330px)';
      }
    },

    dynamicCardsHeight() {
      if (this.filtersExpanded) {
        return 'calc(100vh - 450px)';
      } else {
        return this.hasActiveFilters ? 'calc(100vh - 350px)' : 'calc(100vh - 450px)';
      }
    },

    tiposDeMaquina() {
      return [
        ...new Set(
          this.maquinas.map(
            (item) => item.tipo_maquina?.tipo_maquinas || "Não Especificado"
          )
        ),
      ];
    },

    setores() {
      return [
        ...new Set(
          this.maquinas.map(
            (item) => item.setor?.descricao_setor || "Não Especificado"
          )
        ),
      ];
    },

    filteredMaquinas() {
      return this.maquinas.filter((maquina) => {
        const searchTerm = this.search?.toLowerCase().trim() || "";
        let isSearchById = false;
        let searchId = null;

        // Verifica busca por ID formatado (MAQ001)
        const maqIdMatch = searchTerm.match(/^maq(\d{3})$/i);
        if (maqIdMatch) {
          isSearchById = true;
          searchId = maqIdMatch[1];
        }

        const tipoMaquina = maquina.tipo_maquina?.tipo_maquinas || "Não Especificado";
        const setor = maquina.setor?.descricao_setor || "Não Especificado";

        // Busca por ID formatado
        if (isSearchById) {
          return maquina.id.toString().padStart(3, '0') === searchId;
        }

        // Busca por ID numérico
        if (!isNaN(searchTerm) && searchTerm !== "") {
          const numericSearch = parseInt(searchTerm, 10);
          return maquina.id === numericSearch;
        }

        // Busca geral
        const matchSearch = !this.search || 
          maquina.descricao_maquina?.toLowerCase().includes(searchTerm) ||
          maquina.numero_serie?.toLowerCase().includes(searchTerm) ||
          tipoMaquina.toLowerCase().includes(searchTerm) ||
          setor.toLowerCase().includes(searchTerm);

        const matchTipoMaquina = !this.filtroTipoMaquina || tipoMaquina === this.filtroTipoMaquina;
        const matchSetor = !this.filtroSetor || setor === this.filtroSetor;

        return matchSearch && matchTipoMaquina && matchSetor;
      });
    },

    // ✅ PAGINAÇÃO PARA CARDS
    totalPages() {
      return Math.ceil(this.filteredMaquinas.length / this.itemsPerPage);
    },

    paginatedCardItems() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredMaquinas.slice(start, end);
    },

    // ✅ CÁLCULOS PARA CARDS
    startCardItem() {
      return ((this.currentPage - 1) * this.itemsPerPage) + 1;
    },

    endCardItem() {
      const end = this.currentPage * this.itemsPerPage;
      return end > this.filteredMaquinas.length ? this.filteredMaquinas.length : end;
    },
  },

  watch: {
    filteredMaquinas() {
      this.currentPage = 1;
    },

    itemsPerPage() {
      this.currentPage = 1;
    },
  },

  methods: {
    // ========================================
    // ✅ MÉTODOS DOS FILTROS EXPANSÍVEIS
    // ========================================

    toggleFilters() {
      this.filtersExpanded = !this.filtersExpanded;
    },

    // ✅ CONTROLAR INDICADOR DE SCROLL
    hideScrollIndicator() {
      this.showScrollIndicator = false;
    },

    // ========================================
    // MÉTODOS PRINCIPAIS
    // ========================================

    async fetchData() {
      this.loadingDialog = true;
      this.mensagemDialag = "Carregando máquinas...";
      
      try {
        const response = await maquinaService.obterTodos();
        this.maquinas = Array.isArray(response) ? response : [];
        
        // Garantir valores padrão
        this.maquinas = this.maquinas.map((item) => ({
          ...item,
          tipo_maquina: item.tipo_maquina || { tipo_maquinas: "Não Especificado" },
          setor: item.setor || { descricao_setor: "Não Especificado" },
          numero_serie: item.numero_serie || "",
          descricao_maquina: item.descricao_maquina || "Sem descrição",
        }));
      } catch (error) {
        console.error("Erro ao buscar máquinas:", error);
        toast.error("Erro ao carregar máquinas");
        this.maquinas = [];
      } finally {
        this.loadingDialog = false;
        this.mensagemDialag = "";
      }
    },

    voltarMenu() {
      this.$router.push({ name: "MenuDefault" }).catch(() => {});
    },

    limparFiltros() {
      this.search = "";
      this.filtroTipoMaquina = null;
      this.filtroSetor = null;
      this.currentPage = 1;
    },

    formatId(id) {
      return id ? String(id).padStart(3, "0") : "000";
    },
    
    getStatusColor(status) {
      const statusMap = {
        'ATIVA': { color: 'success', text: 'Ativa' },
        'INATIVA': { color: 'error', text: 'Inativa' },
        'MANUTENCAO': { color: 'warning', text: 'Manutenção' },
        'OCUPADA': { color: 'info', text: 'Ocupada' },
        'DISPONIVEL': { color: 'success', text: 'Disponível' }
      };
      return statusMap[status] || { color: 'grey', text: 'Indefinido' };
    },
    
    getStatusText(status) {
      const statusMap = {
        'ATIVA': 'Ativa',
        'INATIVA': 'Inativa',
        'MANUTENCAO': 'Manutenção',
        'OCUPADA': 'Ocupada',
        'DISPONIVEL': 'Disponível'
      };
      return statusMap[status] || 'Indefinido';
    },

    // ========================================
    // MÉTODOS DE MODAL (mantidos iguais)
    // ========================================

    openModal(item) {
      this.selectedItemId = item.id;
      this.modalVisible = true;
    },

    closeModal() {
      this.modalVisible = false;
      this.selectedItemId = null;
    },

    handleMaquinaSelecionada(maquina) {
      console.log("Máquina selecionada:", maquina);
      this.modalVisible = false;
    },

    // ========================================
    // MÉTODOS DE ETIQUETA (mantidos iguais)
    // ========================================

    imprimirEtiqueta(maquina) {
      this.maquinaEtiquetaSelecionada = maquina.id;
      this.dialogConfirmarEtiqueta = true;
    },

    fecharModalEtiquita() {
      this.maquinaEtiquetaSelecionada = null;
      this.dialogConfirmarEtiqueta = false;
    },

    async confirmaImpressaoEtiqueta() {
      this.loadingDialog = true;
      this.mensagemDialag = "Imprimindo etiqueta...";

      try {
        const response = await maquinaService.imprimitEtiqueta(this.maquinaEtiquetaSelecionada);

        if (response?.success) {
          toast.success(response.message);
        } else {
          toast.error(response?.message || "Erro ao imprimir etiqueta!");
        }
      } catch (error) {
        console.error("Erro ao imprimir etiqueta:", error);
        toast.error("Erro ao comunicar-se com o servidor.");
      } finally {
        this.loadingDialog = false;
        this.mensagemDialag = "";
        this.fecharModalEtiquita();
      }
    },
  },

  mounted() {
    // ✅ Auto-hide do indicador de scroll após 5 segundos
    setTimeout(() => {
      this.hideScrollIndicator();
    }, 5000);
  },

  created() {
    this.fetchData();
  },
};
</script>

<style scoped>
/* ✅ ESTILOS BÁSICOS (mantidos iguais) */
.maquinas-container {
  padding: 24px;
  background: #e4edfb;
  min-height: 100vh;
}

.page-header {
  margin-bottom: 12px;
}

.back-btn {
  font-weight: 600;
  border-radius: 8px;
}

/* ✅ ESTILOS DOS FILTROS EXPANSÍVEIS (mantidos iguais) */
.filters-card {
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 24px;
  transition: all 0.3s ease;
}

  
.filters-header {
  background: linear-gradient(135deg, #4985c1 0%, #5688c1 100%); /* Linha alterada: gradiente semelhante à cor primary */
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
}

.filters-header:hover {
  background: linear-gradient(135deg, #4985c1 0%, #5688c1 100%); /* Linha alterada: mantém gradiente primary no hover */
  transform: translateY(-1px);
  box-shadow: 0 2px 12px rgba(25, 118, 210, 0.3); /* Linha alterada: sombra em tom de primary */
}
 


.filters-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1976d2;
}

.cursor-pointer {
  cursor: pointer;
}

.expand-btn .v-icon {
  transition: transform 0.3s ease;
}

.rotate-180 {
  transform: rotate(180deg);
}

/* ✅ BARRA DE FILTROS RÁPIDOS */
.quick-filters-bar {
  background: linear-gradient(135deg, #f8f9fa 0%, #fff3e0 100%);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

/* ✅ CONTEÚDO COM ALTURA OTIMIZADA */
.content-area {
  transition: all 0.3s ease;
}

.table-card,
.no-results-card {
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 24px;
}

.search-field {
  border-radius: 8px;
}

/* ✅ INDICADOR DE SCROLL HORIZONTAL */
.scroll-indicator {
  position: relative;
  z-index: 5;
}

/* ✅ WRAPPER DA TABELA COM SCROLL HORIZONTAL */
.table-wrapper {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
}

/* ✅ CUSTOMIZAÇÃO DA TABELA COM SCROLL HORIZONTAL E COLUNA FIXA */
.scrollable-table {
  border-radius: 8px;
}

.scrollable-table :deep(.v-table__wrapper) {
  overflow-x: auto !important;
  overflow-y: auto !important;
  position: relative;
}

/* ✅ COLUNA ID FIXA */
.scrollable-table :deep(.v-data-table__th:first-child),
.scrollable-table :deep(.v-data-table__td:first-child) {
  position: sticky !important;
  left: 0 !important;
  z-index: 10 !important;
  background: white !important;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1) !important;
}

.scrollable-table :deep(.v-data-table__th:first-child) {
  z-index: 15 !important;
  background: #f8f9fa !important;
}

.fixed-column-content {
  min-width: 100px;
  display: flex;
  align-items: center;
}

/* ✅ LARGURAS MÍNIMAS DAS COLUNAS */
.scrollable-table :deep(.v-data-table__th),
.scrollable-table :deep(.v-data-table__td) {
  white-space: nowrap;
  min-width: 120px;
}

.scrollable-table :deep(.v-data-table__th:first-child),
.scrollable-table :deep(.v-data-table__td:first-child) {
  min-width: 120px !important;
  max-width: 120px !important;
}

/* ✅ HEADERS DA TABELA */
.scrollable-table :deep(.v-data-table__th) {
  font-weight: 600 !important;
  background-color: #f8f9fa !important;
  position: sticky !important;
  top: 0 !important;
  z-index: 10 !important;
}

/* ✅ SCROLL CUSTOMIZADO PARA TABELA */
.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #1976d2, #2196f3);
  border-radius: 4px;
}

.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #1565c0, #1976d2);
}

.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar-corner {
  background: #f1f1f1;
}

/* ✅ TRUNCATE PARA CÉLULAS LONGAS */
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ✅ ESTILOS DOS CARDS DE MÁQUINAS */
.cards-container {
  width: 100%;
  padding: 4px;
}

/* Card principal */
.device-card {
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #e0e0e0;
  position: relative;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  margin-bottom: 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.device-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08) !important;
  border-color: #bbdefb;
}

/* Cabeçalho do card */
.device-card-header {
  background: #f8fafc;
  border-bottom: 1px solid #eef2f6;
  padding: 14px 16px;
  min-height: 64px;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  position: relative;
}

.device-card:hover .device-card-header {
  background: #f1f5f9;
}

/* Avatar do cabeçalho */
.device-card-header .v-avatar {
  background-color: rgba(30, 136, 229, 0.1) !important;
  margin-right: 12px;
}

.device-card-header .v-icon {
  color: #1e88e5 !important;
}

/* Status Chip */
.status-chip {
  position: absolute;
  top: 12px;
  right: 12px;
  font-size: 0.675rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

/* Estilos para os itens de informação */
.info-item {
  margin-bottom: 12px;
}

.info-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #546e7a;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
}

.info-label .v-icon {
  margin-right: 6px;
  font-size: 16px;
}

.info-value {
  font-size: 0.95rem;
  font-weight: 500;
  color: #263238;
  word-break: break-word;
  line-height: 1.4;
}

/* Ajustes para os botões de ação */
.v-card-actions {
  padding: 12px 16px !important;
  background-color: #f8fafc !important;
  border-top: 1px solid #eef2f6 !important;
  margin-top: auto;
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.v-card-actions .v-btn {
  flex: 1;
  min-width: 0;
  text-transform: none;
  letter-spacing: normal;
  font-weight: 500;
}

/* Efeito de hover nos botões de ação */
.v-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Ajustes para os chips dentro dos cards */
.v-chip {
  font-weight: 500;
  letter-spacing: 0.3px;
  height: 26px;
  font-size: 0.75rem;
}

/* Seção de informações da máquina */
.machine-info-section {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #e0e0e0;
}

/* Ajustes para a área de scroll dos cards */
.cards-scroll-area {
  overflow-y: auto;
  padding: 4px;
  scrollbar-width: thin;
}

/* Estilização da barra de rolagem */
.cards-scroll-area::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.cards-scroll-area::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.cards-scroll-area::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 3px;
}

/* Estilos para a paginação desktop */
.page-size-select {
  max-width: 80px;
  margin-top: -4px;
}

.page-size-select .v-field {
  min-height: 32px !important;
  padding-top: 0;
  padding-bottom: 0;
}

.page-size-select .v-field__input {
  padding: 0 8px !important;
  min-height: 32px !important;
}

/* Ajustes para telas pequenas */
@media (max-width: 960px) {
  .pagination-container > div:not(.mobile-pagination) {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .pagination-container .page-size-select {
    width: 100%;
    max-width: 100%;
  }
}

@media (max-width: 600px) {
  .device-card-header {
    flex-direction: column;
    align-items: flex-start;
    padding-bottom: 8px;
  }
  
  .status-chip {
    position: static;
    margin-top: 8px;
    align-self: flex-start;
  }
  
  .v-card-actions {
    flex-direction: column;
    gap: 8px;
  }
  
  .v-card-actions .v-btn {
    width: 100%;
  }
}

/* ✅ SCROLL CUSTOMIZADO PARA CARDS */
.cards-scroll-area::-webkit-scrollbar {
  width: 6px;
}

.cards-scroll-area::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.cards-scroll-area::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #1976d2, #2196f3);
  border-radius: 3px;
}

.cards-scroll-area::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #1565c0, #1976d2);
}

/* ✅ ESTILOS DE PAGINAÇÃO MOBILE */
.mobile-page-indicator {
  position: sticky;
  top: 0;
  z-index: 5;
}

.page-info-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%);
  border-radius: 12px;
}

.mobile-quick-nav {
  margin-bottom: 16px;
}

.mobile-pagination {
  width: 100%;
}

.pagination-card {
  border-radius: 16px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
}

.nav-btn {
  min-width: 48px;
  height: 48px;
  border-radius: 12px;
}

.page-display {
  flex: 1;
  display: flex;
  justify-content: center;
}

.page-slider {
  margin: 16px 0;
}

.page-slider-component {
  margin: 0 16px;
}

/* ✅ BOTÕES FLUTUANTES */
.floating-nav {
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 16px;
  z-index: 1000;
}

.floating-btn {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
}

.floating-prev {
  animation: slideInLeft 0.3s ease;
}

.floating-next {
  animation: slideInRight 0.3s ease;
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-100px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(100px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* ✅ ESTILOS DE PAGINAÇÃO */
.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px 0;
  background: white;
  border-radius: 8px;
  margin-top: 16px;
}

/* ✅ ESTILOS DOS MODAIS */
.loading-card,
.confirmation-card {
  border-radius: 16px;
}

/* ✅ RESPONSIVIDADE */
@media (max-width: 768px) {
  .maquinas-container {
    padding: 16px;
  }
  
  .page-header .d-flex {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 16px;
  }
  
  .back-btn {
    align-self: stretch;
  }
  
  .machine-card {
    margin-bottom: 16px;
  }
  
  .cards-scroll-area {
    max-height: calc(100vh - 300px) !important;
  }
  
  .mobile-quick-nav .v-btn {
    font-size: 0.875rem;
    padding: 8px 16px;
  }
  
  .nav-btn {
    min-width: 44px;
    height: 44px;
  }
  
  .floating-nav {
    bottom: 24px;
  }
  
  /* Filtros em mobile */
  .quick-filters-bar .d-flex {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 8px;
  }
  
  /* ✅ TABELA EM MOBILE - SCROLL HORIZONTAL OTIMIZADO */
  .scrollable-table :deep(.v-data-table__th),
  .scrollable-table :deep(.v-data-table__td) {
    min-width: 100px;
    font-size: 0.875rem;
  }
  
  .scrollable-table :deep(.v-data-table__th:first-child),
  .scrollable-table :deep(.v-data-table__td:first-child) {
    min-width: 100px !important;
    max-width: 100px !important;
  }
  
  .scroll-indicator {
    margin: 8px;
  }
}

/* ✅ RESPONSIVIDADE PARA TABLETS */
@media (min-width: 769px) and (max-width: 1024px) {
  .scrollable-table :deep(.v-data-table__th),
  .scrollable-table :deep(.v-data-table__td) {
    min-width: 140px;
  }
  
  .scrollable-table :deep(.v-data-table__th:first-child),
  .scrollable-table :deep(.v-data-table__td:first-child) {
    min-width: 120px !important;
    max-width: 120px !important;
  }
}

/* ✅ ANIMAÇÕES */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.machine-card {
  animation: slideInUp 0.3s ease;
}

.machine-card:nth-child(1) { animation-delay: 0.1s; }
.machine-card:nth-child(2) { animation-delay: 0.2s; }
.machine-card:nth-child(3) { animation-delay: 0.3s; }
.machine-card:nth-child(4) { animation-delay: 0.4s; }

/* ✅ ANIMAÇÃO DO MODAL */
@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.confirmation-card {
  animation: modalSlideIn 0.3s ease;
}

/* ✅ ANIMAÇÃO DE EXPANSÃO */
.v-expand-transition-enter-active,
.v-expand-transition-leave-active {
  transition: all 0.3s ease;
}

.v-expand-transition-enter-from,
.v-expand-transition-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* ✅ ANIMAÇÃO DO INDICADOR DE SCROLL */
@keyframes fadeInOut {
  0% { opacity: 0; transform: translateY(-10px); }
  10% { opacity: 1; transform: translateY(0); }
  90% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-10px); }
}

.scroll-indicator {
  animation: fadeInOut 5s ease-in-out;
}

/* ✅ CUSTOMIZAÇÃO DOS CHIPS */
.v-chip.v-chip--size-small {
  font-size: 0.75rem;
  height: 24px;
}

.v-chip.v-chip--size-x-small {
  font-size: 0.625rem;
  height: 20px;
}

/* ✅ MELHORIAS NA TIPOGRAFIA */
.text-h6 {
  line-height: 1.3;
}

.info-label {
  letter-spacing: 0.5px;
}

/* ✅ SOMBRAS CUSTOMIZADAS */
.machine-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.machine-card:hover {
  box-shadow: 0 8px 25px rgba(25, 118, 210, 0.15);
}

/* ✅ BORDAS ARREDONDADAS CONSISTENTES */
.v-card,
.v-btn,
.v-text-field,
.v-select {
  border-radius: 8px !important;
}

.filters-card,
.table-card,
.no-results-card {
  border-radius: 12px !important;
}

/* ✅ TRANSIÇÕES SUAVES */
* {
  transition: all 0.2s ease;
}

/* ✅ FOCUS STATES */
.v-text-field:focus-within,
.v-select:focus-within {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15);
}

/* ✅ SCROLLBAR PARA MOBILE */
@media (max-width: 768px) {
  .cards-scroll-area::-webkit-scrollbar {
    width: 4px;
  }
  
  .scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
}

/* ✅ PERFORMANCE OPTIMIZATIONS */
.machine-card,
.scrollable-table {
  will-change: transform;
}

/* ✅ ACESSIBILIDADE */
.v-btn:focus {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

/* ✅ ESTADOS DE LOADING */
.machine-card[loading="true"] {
  pointer-events: none;
  opacity: 0.7;
}

/* ✅ HOVER EFFECTS PARA LINHAS DA TABELA */
.scrollable-table :deep(.v-data-table__tr:hover) {
  background-color: rgba(25, 118, 210, 0.04) !important;
}

/* ✅ INDICADOR VISUAL DE SCROLL ATIVO */
.table-wrapper::-webkit-scrollbar-thumb:active {
  background: linear-gradient(135deg, #1565c0, #0d47a1);
}

/* ✅ PRINT STYLES */
@media print {
  .filters-card,
  .pagination-container,
  .scroll-indicator,
  .floating-nav {
    display: none;
  }
  
  .machine-card {
    break-inside: avoid;
    margin-bottom: 16px;
  }
  
  .confirmation-card {
    box-shadow: none;
    border: 1px solid #ddd;
  }
  
  .scrollable-table :deep(.v-data-table__th:first-child),
  .scrollable-table :deep(.v-data-table__td:first-child) {
    position: static !important;
    box-shadow: none !important;
  }
}

/* ✅ DARK MODE SUPPORT */
@media (prefers-color-scheme: dark) {
  .scrollable-table :deep(.v-data-table__th:first-child),
  .scrollable-table :deep(.v-data-table__td:first-child) {
    background: #1e1e1e !important;
  }
  
  .scrollable-table :deep(.v-data-table__th:first-child) {
    background: #2d2d2d !important;
  }
}
.desktop-top-pagination {
  position: sticky;      /* Linha adicionada */
  top: 0;                /* Linha adicionada */
  background: white;     /* Linha adicionada */
  z-index: 5;            /* Linha adicionada */
}


</style>